import { AfterViewInit, ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import {
  Agent,
  AgentService,
  FormView,
  FormViewService,
  getClientSubmissionPath,
  IDataPullType,
  IndexSummary,
  IntegrationMappingService,
  JsonbType,
  NotificationService,
  Query,
  QueryRule,
  Submission,
  SubmissionService
} from '@xilo-mono/form-contracts';
import { getFormFieldByKey, tokenIsExpired } from '@xilo-mono/form-helper';
import { cloneDeep, isEmpty, set } from 'lodash';
import { catchError, debounceTime, EMPTY, map, Observable, of, ReplaySubject, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { CustomerFormFacade } from './+state/customer-form/customer-form.facade';
import * as SubmissionActions from './+state/submission/submission.actions';
import { submitForm, updateSubmission } from './+state/submission/submission.actions';
import { submissionQuery } from './+state/submission/submission.selectors';
import { FailureModalComponent, LoadingModalComponent, SelectionModalComponent, SDKPolicyModalComponent } from './components/data-pull-modal';
import { IDataPullSection } from './interfaces/pull-data-group.interface';
import { AuthService } from '@xilo-mono/xilo-services';
import { throttleTime, distinctUntilChanged, BehaviorSubject } from 'rxjs';

const { getSubmissionLoaded, getSubmissionError, getSubmissionEntity } = submissionQuery;

@Component({
  selector: 'xilo-mono-form-viewer',
  templateUrl: './form-viewer.component.html'
})
export class FormViewerComponent implements OnInit, AfterViewInit, OnDestroy, DoCheck {
  @Input() fields: FormlyFieldConfig[];
  @Input() options: FormlyFormOptions;
  @Input() isValid: boolean;
  @Input() form: FormGroup;
  @Input() formView: FormView;
  @Input() model = {};
  @Input() preview = false;
  @Input() agent: Agent;

  @Output() showAlert = new EventEmitter<{ type: string; message: string; link?: string }>();
  @Output() isLoading = new EventEmitter<boolean>();
  @Output() resetSubmission = new EventEmitter();
  @Output() formCompleted = new EventEmitter();
  @Output() agentAssigned: EventEmitter<Agent | null> = new EventEmitter(null);
  queryParams: Params = Object.assign({}, this.route.snapshot?.queryParams);
  submission$: Observable<Submission>;
  loading$: Observable<boolean>;
  error$: Observable<unknown>;
  customerFormMetadata = {};
  loadingDialog: MatDialogRef<LoadingModalComponent>;
  failureDialog: MatDialogRef<FailureModalComponent>;
  selectionDialog: MatDialogRef<SelectionModalComponent>;
  sdkPolicyDialog: MatDialogRef<SDKPolicyModalComponent>;
  cancelledLoadingDialog = false;
  indexSummary: IndexSummary;
  private formModelIsLoaded = false;
  private dataPullTriggered = false;
  private sectionIndexCache = new Map<string, number>();
  private fieldIdToSectionIndex: Map<string, number>;

  private get isIntakeForm() {
    return this.fields && this.fields[0] && this.fields[0].type === 'intake-form';
  }

  private get formType() {
    return this.isIntakeForm ? 'intake-form' : 'customer-form';
  }

  private get token() {
    return localStorage.getItem('token');
  }

  private get tokenIsActive() {
    return !tokenIsExpired(this.token);
  }

  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  private queryResultCache = new Map<string, { result: boolean; timestamp: number; ttl: number }>();
  private fieldValueCache = new Map<string, { values: any[]; timestamp: number; ttl: number }>();
  private changedFields = new Set<string>();
  private readonly CACHE_TTL = 60_000;
  private readonly MAX_CACHE_SIZE = 1000;
  private fieldChanges$ = new BehaviorSubject<Set<string>>(new Set());
  private lastFormState: { form: FormGroup; valid: boolean } | null = null;
  private formStateChange$ = new Subject<{ form: FormGroup; valid: boolean }>();

  constructor(
    private cdr: ChangeDetectorRef,
    private actionsSubject: ActionsSubject,
    private agentService: AgentService,
    private integrationMappingService: IntegrationMappingService,
    private formViewService: FormViewService,
    private submissionService: SubmissionService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private store: Store,
    private customerFormFacade: CustomerFormFacade,
    public dialog: MatDialog,
    private authService: AuthService
  ) {
    this.loading$ = this.store.pipe(select(getSubmissionLoaded));
    this.error$ = this.store.pipe(select(getSubmissionError));
    this.submission$ = this.store.pipe(select(getSubmissionEntity));
  }

  ngOnInit() {
    this.getAgent();
    this.initializeCaches();
    this.setupOptimizedFormSubscriptions();
    this.setupConditionalLogicOptimization();
    this.setupFormStateTracking();

    this.submission$.pipe(takeUntil(this.destroyed$)).subscribe(retSubmission => {
      if (retSubmission.responses) {
        this.model = cloneDeep(retSubmission.responses) || {};
      }
      if (retSubmission.metadata) {
        this.submissionService.metadata = cloneDeep(retSubmission.metadata);
      }
    });

    this.submissionService.updateMetadata.pipe(takeUntil(this.destroyed$)).subscribe(updateObj => {
      if (this.isIntakeForm) {
        if (updateObj.key) {
          if (!isEmpty(updateObj.value)) {
            this.submissionService.metadata[updateObj.key] = updateObj.value;
          }
        }
      } else {
        if (this.indexSummary?.subsectionIndex === 0) {
          if (updateObj.key) {
            this.submissionService.metadata[updateObj.key] = updateObj.value;
          }
        }
      }
    });

    this.integrationMappingService.pullData
      .pipe(
        switchMap((data: { id: number; index: number; openLoadDialog?: boolean; type: IDataPullType }) => {
          this.cancelledLoadingDialog = false;
          this.queryParams = Object.assign({}, this.route.snapshot.queryParams);
          if (!data?.id) {
            return EMPTY;
          }
          if (!data.index && this.queryParams.subsection) {
            data.index = this.queryParams.subsection;
          }
          return this.integrationMappingService.getIntegrationMappingById(data?.id).pipe(
            map(integrationMapping => ({
              data,
              integrationMapping
            }))
          );
        }),
        switchMap(({ data, integrationMapping }) => {
          let isSDKPull = false;
          if (integrationMapping?.vendor?.organization === 'APPLIEDSDK') isSDKPull = true;
          this.loadingDialog = this.dialog.open(LoadingModalComponent, {
            panelClass: 'xilo-lib-dialog--sm',
            disableClose: true,
            data: {
              id: data?.id,
              loadSeconds: isSDKPull ? 200 : data.type.loadSeconds,
              description: isSDKPull
                ? 'This may take a few minutes while we work our magic to pull the data from the address.'
                : `${data.type.type === 'OCR' ? 'This may take up to 1 minute while we work our magic to pull the data from the document.' : ''}`,
              buttonText: `${data.type.type === 'OCR' ? 'Cancel Upload' : ''}`
            },
            position: { top: '5rem' }
          });
          this.loadingDialog
            .afterClosed()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(result => {
              if (result?.cancelled) this.cancelledLoadingDialog = true;
            });
          if (isSDKPull) return this.pullSDKClientAndPolicies(data, integrationMapping, 'getClientAndPolicies');
          return this.pullDataByIntegrationMapping(data);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();

    this.formViewService.submitForm.pipe(takeUntil(this.destroyed$)).subscribe(submit => {
      if (submit) {
        this.onSubmit();
      }
    });
    this.formViewService.assignAgent.pipe(takeUntil(this.destroyed$)).subscribe(assignAgent => {
      if (assignAgent) {
        this.assignAgent();
      }
    });
    this.actionsSubject.pipe(ofType(SubmissionActions.submitFormSuccess), takeUntil(this.destroyed$)).subscribe((data: { clientId: number; token?: string }) => {
      const { clientId, token } = data;
      if (clientId) {
        if (this.isIntakeForm) {
          this.onAlert('success', 'Form submitted');
          const params = {
            clientId: +clientId,
            formId: this.formView.metadata['key'],
            referredBy: this.queryParams.ref,
            version: this.formView.metadata['version'],
            agentId: this.formView.metadata['agentId'] || parseInt(this.queryParams.agentId) || 0,
            attachPDF: this.formView.metadata['attachPDF'],
            submissionId: +this.queryParams.submissionId,
            formEvent: 'Finished'
          };
          if (!this.formView.metadata.notificationOptions) {
            this.notificationService
              .sendOldNotification(params)
              .pipe(takeUntil(this.destroyed$))
              .subscribe({
                error: error => console?.error(error)
              });
          }
        } else {
          this.formCompleted.emit();
          this.customerFormFacade.updateFormStatus('Finished');
        }
      }
      if (this.submissionService.filesForUpload?.length > 0) {
        this.submissionService
          .uploadFiles(this.submissionService.filesForUpload, clientId, token)
          .pipe(takeUntil(this.destroyed$))
          .subscribe(() => {
            this.submissionService.filesForUpload = [];
          });
      }
    });
    this.formViewService.rebuildForm.pipe(takeUntil(this.destroyed$)).subscribe(rebuild => {
      if (rebuild) {
        this.rebuildForm();
      }
    });
    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(p => {
      if (p) {
        this.queryParams = Object.assign({}, this.route.snapshot.queryParams);
      }
    });

    this.customerFormFacade.indexSummary$.pipe(takeUntil(this.destroyed$)).subscribe(summary => {
      this.indexSummary = summary;
    });

    this.customerFormFacade.metadata$.pipe(takeUntil(this.destroyed$)).subscribe(metadata => {
      this.customerFormMetadata = metadata;
    });

    if (this.preview) {
      this.customerFormFacade.setPreviewMode(true);
    }
    this.formViewService.model = this.model;
    this.formViewService.form = this.form;
    this.formViewService.formView = this.formView;

    if (this.fields?.[0]?.type === 'customer-form') {
      this.setHideExpressions(this.fields, true);
    } else {
      this.setHideExpressions(this.fields, false);
    }
  }

  ngAfterViewInit(): void {
    this.formViewService.intakeAnother$.pipe(takeUntil(this.destroyed$)).subscribe(isAnotherSubmission => {
      if (isAnotherSubmission) {
        // TODO : add another
        // this.clearModel(this.model);
        // this.form.reset();
        // this.fields.forEach((field) => {
        //   this.resetFieldArrays(field);
        // });
        // this.formViewService.formSubmitted$.next();
      }
      this.cdr.detectChanges();
    });
  }

  ngDoCheck() {
    this.checkFormStateChange();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  getAgent() {
    if (!this.agent && this.queryParams.agentId) {
      this.agentService
        .getAgentEmailById(this.queryParams.agentId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          agent => {
            this.agent = agent;
          },
          error => {
            console?.error(error);
          }
        );
    }
  }

  private rebuildForm() {
    this.initializeCaches();
    if (this.options && this.options['_buildForm'] && typeof this.options['_buildForm'] === 'function') {
      try {
        this.options['_buildForm'](true);
      } catch (error) {
        console?.error(error);
      }
    } else {
      console?.warn('_buildForm is not defined', this.options);
    }
  }

  private onAlert(type: string, message: string, link?: string) {
    this.showAlert.emit({ type, message, link });
  }

  private onUpdate(): Observable<void> {
    this.queryParams = Object.assign({}, this.route.snapshot.queryParams);
    const { submissionId } = this.queryParams;
    const url = window.location.href;
    if (!submissionId || !this.tokenIsActive) {
      if (this.isIntakeForm)
        this.onAlert(
          'warn',
          'Your session has expired so we have reset the form. To continue updating an existing Clients form, please visit and re-open their form from the Dashboard',
          getClientSubmissionPath('agent', submissionId, url)
        );
      this.resetSubmission.emit();
      return of(void 0);
    }
    const metadata = {
      ...this.submissionService.metadata,
      ...this.queryParams
    };
    if (!metadata.agentId && this.queryParams.agentId) metadata.agentId = this.queryParams.agentId;
    if (!metadata.refferedBy && this.queryParams.ref) metadata.referredBy = this.queryParams.ref;
    const submissionObj = new Submission(this.queryParams.submissionId, metadata, cloneDeep(this.model), null);
    return this.submissionService.updateSubmission(submissionObj).pipe(
      map(() => void 0),
      takeUntil(this.destroyed$)
    );
  }

  public onSubmit() {
    this.queryParams = Object.assign({}, this.route.snapshot?.queryParams);
    let metadata = {};
    if (this.isIntakeForm) {
      metadata = {
        ...this.submissionService.metadata,
        ...this.queryParams,
        referredBy: this.queryParams.ref,
        formType: this.formType
      };
    } else {
      metadata = {
        ...this.submissionService.metadata,
        ...this.queryParams,
        ...this.customerFormMetadata,
        formType: this.formType
      };
    }
    const submissionObj = new Submission(
      this.queryParams.submissionId,
      metadata,
      cloneDeep(this.model),
      null // what is the expected status of a new submission?
    );
    this.store.dispatch(updateSubmission({ submission: submissionObj }));
    this.store.dispatch(
      submitForm({
        clientId: this.queryParams.clientId,
        formId: this.queryParams.formId,
        companyId: this.queryParams.companyId,
        submissionId: this.queryParams.submissionId,
        agentId: this.queryParams.agentId,
        formType: this.formType
      })
    );
    this.integrationMappingService.pullData.next(null);
  }

  private resetFieldArrays(field: FormlyFieldConfig) {
    if (field.fieldArray) {
      while (field.fieldGroup?.length > 1 && field?.templateOptions?.onRemove) {
        field.templateOptions.onRemove(field.fieldGroup.length - 1);
      }
      field.fieldArray.fieldGroup.forEach(f => {
        this.resetFieldArrays(f);
      });
    } else if (field.fieldGroup) {
      field.fieldGroup.forEach(f => {
        this.resetFieldArrays(f);
      });
    }
  }

  private clearModel(model: unknown) {
    if (model && typeof model === 'object') {
      Object.keys(model).forEach(key => {
        let value = model[key];
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value = [value[0]];
            model[key] = [value[0]];
            this.clearModel(value[0]);
          }
        } else if (typeof value === 'object') {
          this.clearModel(value);
        } else {
          value = null;
        }
      });
    }
  }

  private isFieldInSameGroup(field: FormlyFieldConfig, parentKeys?: string[]): boolean {
    if (!field?.templateOptions?.query?.rules?.length) {
      return false;
    }

    const query = field.templateOptions.query;
    const parents = parentKeys || this.getParentKeys(field);
    if (!parents.length) {
      return false;
    }

    for (const rule of query.rules) {
      const ruleParentKeys = rule.parents || [];

      if (ruleParentKeys.length === 0) {
        continue;
      }
      if (ruleParentKeys.length === 1 && parents.includes(ruleParentKeys[0])) {
        return true;
      } else if (ruleParentKeys.length === 2) {
        // For two parent keys, both must match
        const allParentsMatch = ruleParentKeys.every(key => parents.includes(key));
        if (allParentsMatch) {
          return true;
        }
      }
    }

    return false;
  }

  private getParentKeys(field: FormlyFieldConfig): string[] {
    const parents: string[] = [];
    let parent = field.parent;

    while (parent) {
      if (parent.key) {
        parents.push(parent.key as string);
      }
      parent = parent.parent;
    }

    return parents;
  }

  private setHideExpressions(fields: FormlyFieldConfig[] | undefined, isCustomerForm: boolean, isRepeatSection?: boolean, parentKeys: string[] = []): void {
    if (!fields?.length) return;

    for (const field of fields) {
      try {
        // Update current parentKeys with this field's key if it exists
        const currentParentKeys = [...parentKeys];
        if (field.key) {
          currentParentKeys.push(field.key as string);
        }

        if (field?.templateOptions?.query && typeof field.hideExpression !== 'function') {
          if (isRepeatSection) {
            // For repeat sections, check if field is in the same group
            const isInSameGroup = this.isFieldInSameGroup(field, currentParentKeys);

            if (!isInSameGroup) {
              field.hideExpression = (model, formState, f) => this.hideExpression(f, isCustomerForm);
            } else {
              if (this.isRepeatScopedConditional(field, currentParentKeys, isRepeatSection)) {
                field.hideExpression = (model, formState, f) => this.hideExpression(f, isCustomerForm);
              } else {
                field.hideExpression = `!(${field.hideExpression})`;
              }
            }
          } else {
            // Normal processing for non-repeat fields
            field.hideExpression = (model, formState, f) => this.hideExpression(f, isCustomerForm);
          }
        }

        if (field.fieldArray?.fieldGroup?.length) {
          // Process each item in the repeat section
          this.setHideExpressions(field.fieldArray.fieldGroup, isCustomerForm, true, currentParentKeys);
        }

        if (field.fieldGroup?.length) {
          this.setHideExpressions(field.fieldGroup, isCustomerForm, isRepeatSection, currentParentKeys);
        }
      } catch (error) {
        console.error('Error setting hide expressions', error);
      }
    }
  }

  private isRepeatScopedConditional(field: FormlyFieldConfig, parentKeys: string[], isRepeatSection: boolean): boolean {
    if (!isRepeatSection) return false;

    const rules = field.templateOptions?.query?.rules;
    if (!Array.isArray(rules) || rules.length < 2) return false;

    return rules.some(rule => Array.isArray(rule.parents) && rule.parents.length === 2 && rule.parents.every(p => parentKeys.includes(p)) && rule.field !== field.key);
  }

  private hideExpression(field: FormlyFieldConfig, isCustomerForm: boolean): boolean {
    const query = field?.templateOptions?.query;
    if (!query || !this.model) {
      field.templateOptions.hidden = false;
      return false;
    }

    const sectionIndex = this.getSectionIndexOfField(field.id, this.fields) ?? 0;

    const hidden = this.evaluateQuery(query, isCustomerForm, query.showWhenTrue, sectionIndex);

    field.templateOptions.hidden = hidden;
    return hidden;
  }

  private getSectionIndexOfField(fieldId: string, fields: FormlyFieldConfig[]): number | undefined {
    if (!fieldId || !fields?.length) return;
    const cacheKey = `${fieldId}-${fields.length}`;

    if (this.sectionIndexCache.has(cacheKey)) {
      return this.sectionIndexCache.get(cacheKey);
    }
    if (!this.fieldIdToSectionIndex) {
      this.fieldIdToSectionIndex = new Map<string, number>();
      this.buildFieldIdToSectionIndexMap(fields);
    }

    if (this.fieldIdToSectionIndex.has(fieldId)) {
      const index = this.fieldIdToSectionIndex.get(fieldId);
      this.sectionIndexCache.set(cacheKey, index);
      return index;
    }
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (field.id === fieldId) {
        this.sectionIndexCache.set(cacheKey, i);
        this.fieldIdToSectionIndex.set(fieldId, i);
        return i;
      }

      if (field.type === 'intake-section' && field.fieldGroup) {
        if (this.isFieldInSection(fieldId, field.fieldGroup)) {
          this.sectionIndexCache.set(cacheKey, i);
          this.fieldIdToSectionIndex.set(fieldId, i);
          return i;
        }
      }

      const subFields = field.fieldGroup ?? field.fieldArray?.fieldGroup;
      if (subFields?.length) {
        const nested = this.getSectionIndexOfField(fieldId, subFields);
        if (nested !== undefined) {
          this.sectionIndexCache.set(cacheKey, i);
          this.fieldIdToSectionIndex.set(fieldId, i);
          return i;
        }
      }
    }

    return;
  }

  private buildFieldIdToSectionIndexMap(fields: FormlyFieldConfig[]): void {
    fields.forEach((field, index) => {
      if (field.id) {
        this.fieldIdToSectionIndex.set(field.id, index);
      }

      if (field.type === 'intake-section' && field.fieldGroup) {
        this.flattenFieldGroup(field.fieldGroup, index);
      }

      const subFields = field.fieldGroup ?? field.fieldArray?.fieldGroup;
      if (subFields?.length) {
        this.buildFieldIdToSectionIndexMap(subFields);
      }
    });
  }

  private flattenFieldGroup(fields: FormlyFieldConfig[], sectionIndex: number): void {
    fields.forEach(field => {
      if (field.id) {
        this.fieldIdToSectionIndex.set(field.id, sectionIndex);
      }

      const subFields = field.fieldGroup ?? field.fieldArray?.fieldGroup;
      if (subFields?.length) {
        this.flattenFieldGroup(subFields, sectionIndex);
      }
    });
  }

  private isFieldInSection(fieldId: string, section: FormlyFieldConfig[]): boolean {
    for (const field of section) {
      if (field.id === fieldId) {
        return true;
      }
    }
    for (const field of section) {
      if (field.fieldGroup) {
        if (this.isFieldInSection(fieldId, field.fieldGroup)) {
          return true;
        }
      } else {
        if (field.fieldArray && field.fieldArray.fieldGroup) {
          if (this.isFieldInSection(fieldId, field.fieldArray.fieldGroup)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private evaluateQuery(query: Query, isCustomerForm: boolean, showWhenTrue = false, currentSectionIndex: number) {
    if (!query.rules || query.rules.length === 0) {
      return false;
    }
    const rulesWithFields = query.rules.filter((r): r is QueryRule => 'field' in r);
    const fieldValueMap = new Map<string, any[]>();

    rulesWithFields.forEach(rule => {
      const cacheKey = `${rule.field}-${rule.parents?.join('.')}-${currentSectionIndex}`;

      if (!fieldValueMap.has(cacheKey)) {
        fieldValueMap.set(cacheKey, this.getValues(rule.field, rule.parents, isCustomerForm, currentSectionIndex));
      }
    });

    // const valuesUsed = Array.from(fieldValueMap.values());
    // const cacheKey = `${JSON.stringify(query)}-${JSON.stringify(valuesUsed)}`;
    const cacheKey = this.createEfficientCacheKey(query, isCustomerForm, currentSectionIndex, fieldValueMap);

    if (this.queryResultCache.has(cacheKey)) {
      const cached = this.queryResultCache.get(cacheKey);
      return cached.result;
    }

    let queryMatched = query.condition === 'and';

    for (const ruleOrQuery of query.rules) {
      let result = false;

      if ('rules' in ruleOrQuery) {
        result = this.evaluateQuery(ruleOrQuery as Query, isCustomerForm, showWhenTrue, currentSectionIndex);
      } else {
        const rule = ruleOrQuery as QueryRule;
        const cacheKey = `${rule.field}-${rule.parents?.join('.')}-${currentSectionIndex}`;
        const values = fieldValueMap.get(cacheKey) ?? [];

        for (const value of values) {
          if (this.evaluateRule(rule, value)) {
            result = true;
            break;
          }
        }
      }

      if (query.condition === 'and' && !result) {
        queryMatched = false;
        break;
      } else if (query.condition === 'or' && result) {
        queryMatched = true;
        break;
      }
    }

    const finalResult = query.showWhenTrue ? !queryMatched : queryMatched;
    this.queryResultCache.set(cacheKey, {
      result: finalResult,
      timestamp: Date.now(),
      ttl: this.CACHE_TTL
    });
    return finalResult;
  }

  private getValues(key: string, parents: string[], isCustomerForm: boolean, currentSectionIndex: number, repeatIndex = 0) {
    if (!this.model || !key) return [];

    const cacheKey = `${key}-${parents?.join('.')}-${currentSectionIndex}-${repeatIndex}-${isCustomerForm}`;

    const valueCache = new Map<string, any[]>();
    if (valueCache.has(cacheKey)) {
      return valueCache.get(cacheKey);
    }

    let valueObj = this.model;
    let values = [];

    try {
      const parentsCopy = [...(parents || [])];

      while (parentsCopy.length > 0) {
        const id = parentsCopy.shift();

        if (isCustomerForm) {
          if (values.length > 1) {
            if (Array.isArray(valueObj)) {
              values = [].concat(...values.map(val => (val || []).map(v => v?.[id]).filter(v => v !== undefined)));
              valueObj = valueObj?.[0]?.[id];
            } else {
              values = values.map(val => val?.[id]).filter(v => v !== undefined);
              valueObj = valueObj?.[id];
            }
          } else {
            if (Array.isArray(valueObj)) {
              values = valueObj.map(val => val?.[id]).filter(v => v !== undefined);
              valueObj = valueObj?.[0]?.[id];
            } else {
              valueObj = valueObj?.[id];
            }
          }
        } else {
          if (Array.isArray(valueObj?.[id])) {
            // Use the repeatIndex when accessing array elements in a repeat section
            valueObj = valueObj[id][parentsCopy.length === 0 ? repeatIndex : currentSectionIndex];
          } else {
            valueObj = valueObj?.[id];
          }
        }
      }

      let result;
      if (isCustomerForm && values.length > 1) {
        result = values.map(val => val?.[key]).filter(v => v !== undefined);
      } else {
        if (Array.isArray(valueObj)) {
          // Handle array values in repeat sections
          result = [valueObj[currentSectionIndex]?.[key]].filter(v => v !== undefined);
        } else {
          result = [valueObj?.[key]].filter(v => v !== undefined);
        }
      }
      valueCache.set(cacheKey, result);
      return result;
    } catch {
      return [];
    }
  }

  private evaluateRule(rule: QueryRule, value: unknown) {
    if (rule.value === 'true') {
      rule.value = true;
    } else if (rule.value === 'false') {
      rule.value = false;
    }

    if (rule.value === undefined || rule.value === null) {
      return false;
    }

    const validComparison = value !== null && rule.value !== null;

    switch (rule.operator) {
      case '=':
        if (Array.isArray(value)) {
          return value.some(v => rule.value === v);
        } else {
          return rule.value === value;
        }
      case 'contains':
        return (value as unknown[]).indexOf(rule.value) > -1;
      case '!=':
        if (Array.isArray(value)) {
          return !value.some(v => rule.value === v);
        }
        return rule.value !== value;
      case 'in':
        if (Array.isArray(value)) {
          return value.some(v => (rule.value as unknown[]).includes(v));
        } else {
          return (rule.value as unknown[]).includes(value);
        }
      case 'not in':
        if (Array.isArray(value)) {
          return !value.some(v => (rule.value as unknown[]).includes(v));
        } else {
          return !(rule.value as unknown[]).includes(value);
        }
      case 'is null':
        return value === null || value === undefined;
      case 'is not null':
        return value !== null && value !== undefined;
      case '<':
        return validComparison ? Number(value) < Number(rule.value) : false;
      case '<=':
        return validComparison ? Number(value) <= Number(rule.value) : false;
      case '>':
        return validComparison ? Number(value) > Number(rule.value) : false;
      case '>=':
        return validComparison ? Number(value) >= Number(rule.value) : false;
      default:
        return false;
    }
  }

  private assignAgent() {
    this.submissionService
      .assignAgent(Number(this.queryParams.submissionId), Number(this.queryParams.clientId))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (agent: Agent) => {
          if (agent) this.agentAssigned.emit(agent);
        },
        error: (e: Error) => console?.error(e)
      });
  }

  private detectAndUpdateDuplicates(section: IDataPullSection): IDataPullSection {
    if (!section.sectionFormData || !section.sectionModelData) {
      return;
    }

    section.values.forEach((mappingGroup, mappingGroupIndex) => {
      const [firstNameField, lastNameField] = mappingGroup.values.filter(field => {
        const firstNameField = field.vendorPath?.toLowerCase()?.includes('drivers') && field.vendorPath?.toLowerCase()?.includes('firstname');
        const lastNameField = field.vendorPath?.toLowerCase()?.includes('drivers') && field.vendorPath?.toLowerCase()?.includes('lastname');
        return firstNameField || lastNameField;
      });

      const firstNameDataPullValue = (firstNameField?.value as string)?.toLowerCase();
      const lastNameDataPullValue = (lastNameField?.value as string)?.toLowerCase();

      if (firstNameDataPullValue && lastNameDataPullValue) {
        mappingGroup.values.forEach(mappingField => {
          // Find the corresponding field group in sectionData
          const matchingFieldGroupIndex = section.sectionModelData.findIndex(formSection => {
            const firstNameFormValue = formSection[firstNameField.key]?.toLowerCase();
            const lastNameFormValue = formSection[lastNameField.key]?.toLowerCase();
            if (firstNameDataPullValue === firstNameFormValue && lastNameDataPullValue === lastNameFormValue) return true;
            else return false;
          });

          if (matchingFieldGroupIndex > -1) mappingGroup['duplicateIndex'] = matchingFieldGroupIndex;
        });
      }
    });
    return section;
  }

  private updateFormWithMappings(mappings: any[], organization: string, type?: IDataPullType) {
    const fieldComponents = this.fields[0];
    for (const mapping of mappings) {
      const formSection = fieldComponents.fieldGroup.find(f => f.key === mapping.sectionKey);
      const section = this.form.value[mapping.sectionKey];
      set(mapping, 'sectionLabel', formSection?.templateOptions?.label);
      set(mapping, 'sectionModelData', section ?? undefined);
      set(mapping, 'sectionFormData', formSection.fieldArray ?? undefined);
      set(mapping, 'section', formSection ?? undefined);
      set(mapping, 'isValid', true);
    }
    mappings = this.groupByArray(mappings, 'sectionLabel');
    for (const mapping of mappings) {
      mapping.section = mapping.values[0]?.section;
      mapping.sectionKey = mapping.values[0]?.sectionKey;
      mapping.sectionModelData = mapping.values[0]?.sectionModelData;
      mapping.sectionFormData = mapping.values[0]?.sectionFormData;
      mapping.values.sort((a, b) => a.index - b.index);
      mapping.values = this.groupByArray(mapping.values, 'index');
      mapping.values = mapping.values.map(group => {
        set(group, 'isValid', true);
        return group;
      });
    }
    if (this.isIntakeForm) mappings.forEach(section => this.detectAndUpdateDuplicates(section));
    this.openDataPullModal(mappings, type, organization);
  }

  private groupByArray(xs, key) {
    return xs.reduce(function (rv, x) {
      const v = key instanceof Function ? key(x) : x[key];
      const el = rv.find(r => r && r.key === v);
      if (el) {
        el.values.push(x);
      } else {
        rv.push({ key: v, values: [x] });
      }
      return rv;
    }, []);
  }

  private nameFields(fieldGroup: FormlyFieldConfig[]): FormlyFieldConfig[] | null {
    if (!fieldGroup || fieldGroup?.length === 0) return null;
    const firstNameField = fieldGroup.find(f => f.type === 'firstName');
    const lastNameField = fieldGroup.find(f => f.type === 'lastName');
    if (firstNameField && lastNameField) return [firstNameField, lastNameField];
    else return null;
  }

  private vehicleFields(fieldGroup: FormlyFieldConfig[]): FormlyFieldConfig[] | null {
    if (!fieldGroup || fieldGroup?.length === 0) return null;
    const vehicleYearField = fieldGroup.find(f => f.templateOptions?.dataKey === 'vehicleYear');
    const vehicleMakeField = fieldGroup.find(f => f.templateOptions?.dataKey === 'vehicleMake');
    const vehicleModelField = fieldGroup.find(f => f.templateOptions?.dataKey === 'vehicleModel');
    if (vehicleYearField && vehicleMakeField && vehicleModelField) return [vehicleYearField, vehicleMakeField, vehicleModelField];
    else return null;
  }

  private addressField(fieldGroup: FormlyFieldConfig[]): FormlyFieldConfig | null {
    if (!fieldGroup || fieldGroup?.length === 0) return null;
    const addressField = fieldGroup.find(f => f.type === 'address');
    if (addressField) return addressField;
    else return null;
  }

  private removeBlankDrivers(section) {
    try {
      const nameFields = this.nameFields(section.sectionFormData.fieldGroup);
      if (!nameFields || nameFields?.length < 2) return;

      const [firstNameField, lastNameField] = nameFields;
      if (firstNameField && lastNameField) {
        const parentSection = this.formView.components[0].fieldGroup?.find(group => group.key === section.sectionKey);
        // Reverse the array to remove from the highest index first
        section.sectionModelData = [...section.sectionModelData].reverse();
        section.sectionModelData = section.sectionModelData.filter((group, reversedIndex, arr) => {
          // Convert reversedIndex back to the original index
          const groupIndex = arr.length - 1 - reversedIndex;
          const firstName = group[firstNameField.key as string];
          const lastName = group[lastNameField.key as string];
          if (!firstName && !lastName) {
            parentSection.templateOptions.onRemove(groupIndex);
            return false;
          }
          return true;
        });
        // Restore original order after filtering (if needed)
        section.sectionModelData.reverse();
      }
    } catch (error) {
      console.error('Error removing blank drivers: ', error);
    }
  }

  private removeBlankVehicles(section) {
    try {
      // Ensure valid vehicle fields and model data exist
      const vehicleFields = this.vehicleFields(section.sectionFormData?.fieldGroup);
      if (!vehicleFields || vehicleFields.length < 3 || !section.sectionModelData?.length) return;
      const [vehicleYearField, vehicleMakeField, vehicleModelField] = vehicleFields;
      if (vehicleYearField && vehicleMakeField && vehicleModelField) {
        const parentSection = this.formView.components[0].fieldGroup.find(group => group.key === section.sectionKey);
        if (!parentSection || !parentSection.templateOptions?.onRemove) {
          console.warn('Skipping remove: parentSection or onRemove not found', parentSection);
          return;
        }
        // Reverse the array before filtering to remove from the highest index first
        section.sectionModelData = [...section.sectionModelData].reverse();
        section.sectionModelData = section.sectionModelData.filter((group, reversedIndex, arr) => {
          // Convert reversedIndex back to the original index
          const groupIndex = arr.length - 1 - reversedIndex;
          const vehicleYear = group[vehicleYearField.key as string];
          const vehicleMake = group[vehicleMakeField.key as string];
          const vehicleModel = group[vehicleModelField.key as string];
          if (!vehicleYear && !vehicleMake && !vehicleModel) {
            parentSection.templateOptions.onRemove(groupIndex);
            return false;
          }
          return true;
        });
        // Restore original order after filtering (if needed)
        section.sectionModelData.reverse();
      }
    } catch (error) {
      console.error('Error removing blank vehicles: ', error);
    }
  }

  private removeBlankProperties(section) {
    try {
      const addressField = this.addressField(section.sectionFormData.fieldGroup);
      if (!addressField) return;

      if (addressField) {
        const parentSection = this.formView.components[0].fieldGroup?.find(group => group.key === section.sectionKey);
        // Reverse the array to remove from the highest index first
        section.sectionModelData = [...section.sectionModelData].reverse();
        section.sectionModelData = section.sectionModelData.filter((group, reversedIndex, arr) => {
          // Convert reversedIndex back to the original index
          const groupIndex = arr.length - 1 - reversedIndex;
          const address = group[addressField.key as string];
          if (!address) {
            parentSection.templateOptions.onRemove(groupIndex);
            return false;
          }
          return true;
        });
        // Restore original order after filtering (if needed)
        section.sectionModelData.reverse();
      }
    } catch (error) {
      console.error('Error removing blank properties: ', error);
    }
  }

  private updateFormWithDataPullMappings(mappings: any[], organization: string) {
    const fieldMappings = [];
    const isXILOPersonalPull = organization?.includes('XILO');
    const isXAIOCRPull = organization?.toLowerCase()?.includes('azure') || organization?.toLowerCase()?.includes('ocr');
    const isAppliedSDKPull = organization?.toLowerCase()?.includes('applied');
    const isRepeatPull = isXILOPersonalPull || isXAIOCRPull || isAppliedSDKPull;
    for (const section of mappings) {
      let index = !this.isIntakeForm ? 0 : section.sectionModelData?.length ?? 0;
      const isDriverPull = isRepeatPull && section.values.some(g => g.values.some(f => f.vendorPath?.toLowerCase()?.includes('drivers') || f.vendorPath?.toLowerCase()?.includes('applicant')));
      const isVehiclePull = isRepeatPull && section.values.some(g => g.values.some(f => f.vendorPath?.toLowerCase()?.includes('vehicles')));
      const isPropertyPull = isRepeatPull && section.values.some(g => g.values.some(f => f.vendorPath?.toLowerCase()?.includes('dwellings')));
      // If sectionFormData is undefined then we do not have a formArray to work with therefore this will fail
      if (section.sectionFormData) {
        if (isDriverPull) {
          this.removeBlankDrivers(section);
          if (this.isIntakeForm) index = section.sectionModelData?.length || 0;
          this.detectAndUpdateDuplicates(section);
        } else if (isVehiclePull) {
          this.removeBlankVehicles(section);
          if (this.isIntakeForm) index = section.sectionModelData?.length || 0;
          this.detectAndUpdateDuplicates(section);
        } else if (isPropertyPull) {
          this.removeBlankProperties(section);
          if (this.isIntakeForm) index = section.sectionModelData?.length || 0;
        }
      }
      section.values.forEach(group => {
        let fields = [];
        if (group.isDuplicate && group.duplicateIndex >= 0 && group.isValid) {
          fields = group.values.map(field => {
            //  This logic is getting bad. But needed to add this to support Estated Data Pull on Repeats
            if (!isRepeatPull) index = field.index ? field.index : index;
            field['index'] = group.duplicateIndex;
            const modelSection = this.model?.[field.sectionKey];
            if (Array.isArray(modelSection)) field['isArray'] = true;
            return field;
          });
        } else if (group.isValid) {
          fields = group.values.map(field => {
            //  This logic is getting bad. But needed to add this to support Estated Data Pull on Repeats
            if (!isRepeatPull) index = !isNaN(field.index) ? field.index : index;
            field['index'] = index;
            const modelSection = this.model?.[field.sectionKey];
            if (Array.isArray(modelSection)) field['isArray'] = true;
            return field;
          });
          index += 1;
        }
        fieldMappings.push(...fields);
      });
    }
    for (const field of fieldMappings) {
      if (field.isValid) {
        if (field.isArray) {
          const section = this.model[field.sectionKey];
          if (section.length < field.index + 1) {
            section.length = field.index + 1;
            this.model = { ...this.model };
            this.form.reset(this.model);
          }
        }
        const response = getFormFieldByKey(this.isIntakeForm, this.form, field.formPath, field.index);
        if (response.success) {
          const { fieldFormGroup } = response;

          // Update the control
          fieldFormGroup.patchValue(field.value);

          // ALSO update the model directly
          const modelPath = field.formPath.split('.');
          if (field.isArray && typeof field.index === 'number') {
            // Ensure path includes index for array items if needed by 'set'
            modelPath.splice(1, 0, field.index.toString());
          }
          set(this.model, modelPath, field.value);
          this.model = { ...this.model };
          this.form.reset(this.model);
        } else {
          // In this case we most likely have a hidden field
          const path = field.formPath.split('.');
          if (field.isArray && typeof field.index === 'number') {
            // Check index type
            path.splice(1, 0, field.index.toString()); // Use toString for key
          }
          set(this.model, path, field.value);
          this.model = { ...this.model };
          this.form.reset(this.model);
        }
      }
    }
    if (!this.isIntakeForm) {
      this.formViewService.fields = this.formView.components[0].fieldGroup;
      this.formViewService.metadata.next(this.formView.metadata);
      this.formViewService.formView = this.formView;
      this.formViewService.model = { ...this.model };
      const fields = cloneDeep(this.formViewService.fields);
      this.initializeCaches();
      this.customerFormFacade.setFields(fields);
    } else {
      this.formViewService.updateForm.next(true);
    }
    if (isXILOPersonalPull) this.dataPullTriggered = true;
  }

  private openDataPullModal(rows: any[], type: IDataPullType, organization: string): void {
    this.selectionDialog = this.dialog.open(SelectionModalComponent, {
      panelClass: 'xilo-lib-dialog',
      data: {
        sections: rows,
        headerText: `${type?.type === 'DATA' ? 'Great News!' : "Let's review the upload results"}`,
        description: `${
          type?.type === 'DATA'
            ? 'We found the following info based on the address provided. Please uncheck any information that does not apply.'
            : 'We were able to pull the info below from your document. Please review and add the relevant info to the form. '
        }`,
        showWarning: type?.type === 'DATA' && this.isIntakeForm && organization?.includes('XILO')
      },
      disableClose: true
    });

    this.selectionDialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(result => {
        if (!result.success) return;
        this.updateFormWithDataPullMappings(result.data, organization);
      });
  }

  private pullPolicyData(policyId: number, clientId: number, data: JsonbType, companyId: number, logId: string, integrationMapping: any) {
    // This is to pull Policy specific information specifically
    return this.integrationMappingService
      .triggerSDKIntegration(
        this.model,
        +data.id,
        this.queryParams.formId,
        +this.queryParams.clientId,
        this.queryParams.companyId,
        data.index,
        +this.queryParams.submissionId,
        companyId,
        'getPolicyData',
        integrationMapping.vendor.type,
        logId,
        { PolicyID: policyId, ClientID: clientId }
      )
      .pipe(
        switchMap(id =>
          this.integrationMappingService.pollSDKLogStatusById(id, false, false).pipe(
            tap((log: JsonbType) => {
              const parsedLog = JSON.parse(log.vendorResponse);
              if (!this.cancelledLoadingDialog && parsedLog.mappingResult.length) {
                this.onCancelLoadingDialog();
                this.updateFormWithMappings(parsedLog.mappingResult, 'APPLIEDSDK', { type: 'DATA', loadSeconds: 200 });
              } else if (!this.cancelledLoadingDialog) {
                this.onFailedDialog('DATA');
              }
            })
          )
        ),
        catchError(() => {
          this.onFailedDialog('DATA');
          return EMPTY;
        })
      );
  }

  private pullSDKClientAndPolicies(
    data: { id: number; index: number; openLoadDialog?: boolean; type: IDataPullType },
    integrationMapping: any,
    job: 'getClientAndPolicies' | 'getPolicyData' | 'push',
    logId?: string,
    selectedClientId?: number
  ) {
    // Pull Client and Policies list first
    const user = this.authService.getUserFromAuth();
    const isPoliciesOnlyPull = selectedClientId ? true : false;
    return this.integrationMappingService
      .triggerSDKIntegration(
        this.model,
        +data.id,
        this.queryParams.formId,
        +this.queryParams.clientId,
        this.queryParams.companyId,
        data.index,
        +this.queryParams.submissionId,
        +user.companyId,
        job,
        integrationMapping.vendor.type,
        logId,
        { ClientID: selectedClientId }
      )
      .pipe(
        switchMap(id =>
          this.integrationMappingService.pollSDKLogStatusById(id, true, isPoliciesOnlyPull).pipe(
            switchMap((log: JsonbType) => {
              if (!this.cancelledLoadingDialog && log.jsonDataIn.Client) {
                this.onCancelLoadingDialog();
                if (log.jsonDataIn.Client?.length && !log.jsonDataIn.Policies?.length) {
                  // Multiple Clients have been found, trigger selection of one
                  this.sdkPolicyDialog = this.dialog.open(SDKPolicyModalComponent, {
                    panelClass: 'xilo-lib-dialog',
                    data: {
                      client: log.jsonDataIn.Client
                    },
                    disableClose: true
                  });

                  return this.sdkPolicyDialog.afterClosed().pipe(
                    switchMap(result => {
                      if (!result.success) return EMPTY;
                      // Trigger policy data pull
                      this.loadingDialog = this.dialog.open(LoadingModalComponent, {
                        panelClass: 'xilo-lib-dialog--sm',
                        disableClose: true,
                        data: {
                          id: data?.id,
                          loadSeconds: 180,
                          description: 'This may take a few minutes while we work our magic to pull the data from the address.',
                          buttonText: ''
                        },
                        position: { top: '5rem' }
                      });
                      this.loadingDialog
                        .afterClosed()
                        .pipe(takeUntil(this.destroyed$))
                        .subscribe(result => {
                          if (result?.cancelled) {
                            this.cancelledLoadingDialog = true;
                          }
                        });
                      this.cancelledLoadingDialog = false;
                      return this.pullSDKClientAndPolicies(data, integrationMapping, 'getClientAndPolicies', log.id, result.data.ClientID);
                    }),
                    takeUntil(this.destroyed$)
                  );
                } else if (log.vendorResponse) {
                  // A client with no policies or a single client and policy has been found
                  const parsedLog = JSON.parse(log.vendorResponse);
                  this.updateFormWithMappings(parsedLog.mappingResult, 'APPLIEDSDK', {
                    type: 'DATA',
                    loadSeconds: 200
                  });
                } else if (log.jsonDataIn.Policies?.length > 1) {
                  // Multiple policies have been found
                  this.sdkPolicyDialog = this.dialog.open(SDKPolicyModalComponent, {
                    panelClass: 'xilo-lib-dialog',
                    data: {
                      policies: log.jsonDataIn.Policies,
                      client: log.jsonDataIn.Client
                    },
                    disableClose: true
                  });

                  return this.sdkPolicyDialog.afterClosed().pipe(
                    switchMap(result => {
                      if (!result.success) return EMPTY;
                      // Trigger policy data pull
                      this.loadingDialog = this.dialog.open(LoadingModalComponent, {
                        panelClass: 'xilo-lib-dialog--sm',
                        disableClose: true,
                        data: {
                          id: data?.id,
                          loadSeconds: 200,
                          description: 'This may take a few minutes while we work our magic to pull the data from the address.',
                          buttonText: ''
                        },
                        position: { top: '5rem' }
                      });
                      this.loadingDialog
                        .afterClosed()
                        .pipe(takeUntil(this.destroyed$))
                        .subscribe(result => {
                          if (result?.cancelled) {
                            this.cancelledLoadingDialog = true;
                          }
                        });
                      this.cancelledLoadingDialog = false;
                      return this.pullPolicyData(
                        result.data.PolicyID || result.data.MarketedPolicyID,
                        result.data.AccountID || result.data.ClientID,
                        data,
                        +user.companyId,
                        log.id,
                        integrationMapping
                      );
                    }),
                    takeUntil(this.destroyed$)
                  );
                } else if (log.jsonDataIn.Client && log.jsonDataIn.Policies) {
                  const policy = Array.isArray(log.jsonDataIn.Policies) ? log.jsonDataIn.Policies[0] : log.jsonDataIn.Policies;
                  const client = log.jsonDataIn.Client;
                  this.cancelledLoadingDialog = false;
                  return this.pullPolicyData(
                    policy.PolicyID || policy.MarketedPolicyID,
                    client.AccountID || client.ClientID,
                    { id: integrationMapping.id, index: integrationMapping.index },
                    +user.companyId,
                    log.id,
                    integrationMapping
                  );
                }
              } else if (!this.cancelledLoadingDialog) {
                this.onFailedDialog('DATA');
              }
              return EMPTY;
            })
          )
        ),
        catchError(() => {
          this.onFailedDialog('DATA');
          return EMPTY;
        })
      );
  }

  private pullDataByIntegrationMapping(data: { id: number; index: number; openLoadDialog?: boolean; type: IDataPullType }) {
    return this.integrationMappingService
      .pullDataByIntegrationMapping(this.model, data.id, this.queryParams.formId, this.queryParams.clientId, this.queryParams.companyId, data.index, this.queryParams.submissionId)
      .pipe(
        switchMap(id =>
          this.integrationMappingService.pollJobStatusById(id, data.type).pipe(
            tap(({ mappings, organization }) => {
              if (!this.cancelledLoadingDialog && mappings.length) {
                this.onCancelLoadingDialog();
                this.updateFormWithMappings(mappings, organization, data.type);
              } else if (!this.cancelledLoadingDialog) {
                this.onFailedDialog(data.type.type);
              }
            })
          )
        ),
        catchError(() => {
          this.onFailedDialog(data.type.type);
          return EMPTY;
        })
      );
  }

  private onCancelLoadingDialog() {
    this.cancelledLoadingDialog = true;
    this.loadingDialog.close();
  }

  private onFailedDialog(type: IDataPullType['type']) {
    this.onCancelLoadingDialog();
    this.failureDialog = this.dialog.open(FailureModalComponent, {
      panelClass: 'xilo-lib-dialog--sm',
      position: { top: '5rem' },
      data: {
        description: `${type === 'OCR' ? 'There was an issue with processing the document. Please re-upload and try again.' : ''}`
      }
    });
    this.integrationMappingService.pullData.next(null);
  }

  private initializeCaches(): void {
    this.queryResultCache.clear();
    this.fieldValueCache.clear();
    this.changedFields.clear();
    this.sectionIndexCache.clear();
  }

  private setupOptimizedFormSubscriptions(): void {
    this.form?.valueChanges
      .pipe(
        throttleTime(100), // Throttle to prevent excessive calls
        debounceTime(500), // Debounce for final evaluation
        distinctUntilChanged((prev, curr) => {
          // Only trigger if relevant fields changed
          const changedKeys = this.getChangedFieldKeys(prev, curr);
          if (changedKeys.size === 0) return true;
          this.changedFields = changedKeys;
          this.fieldChanges$.next(changedKeys);
          return false;
        }),
        switchMap(changes => {
          if (changes && this.formModelIsLoaded) {
            return this.onUpdate();
          }
          return of(null);
        }),
        tap(() => (this.formModelIsLoaded = true)),
        takeUntil(this.destroyed$)
      )
      .subscribe();
  }

  private setupConditionalLogicOptimization(): void {
    // Only evaluate conditional logic for fields that depend on changed values
    this.fieldChanges$
      .pipe(
        debounceTime(200), // Batch multiple field changes
        switchMap((changedFields: Set<string>) => {
          if (changedFields.size === 0) return of(null);

          const fieldsToUpdate = this.getFieldsAffectedByChanges(changedFields);
          return this.updateConditionalFields(fieldsToUpdate);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
  }

  private getChangedFieldKeys(prev: any, curr: any): Set<string> {
    const changedKeys = new Set<string>();

    const compareObjects = (prevObj: any, currObj: any, path = '') => {
      if (!prevObj || !currObj) return;

      const allKeys = new Set([...Object.keys(prevObj), ...Object.keys(currObj)]);

      for (const key of allKeys) {
        const currentPath = path ? `${path}.${key}` : key;
        const prevValue = prevObj[key];
        const currValue = currObj[key];

        // Use efficient comparison instead of JSON.stringify
        if (!this.isValueEqual(prevValue, currValue)) {
          changedKeys.add(currentPath);

          // Recursively check nested objects
          if (typeof prevValue === 'object' && typeof currValue === 'object') {
            compareObjects(prevValue, currValue, currentPath);
          }
        }
      }
    };

    compareObjects(prev, curr);
    return changedKeys;
  }
  private getFieldsAffectedByChanges(changedFields: Set<string>): FormlyFieldConfig[] {
    const affectedFields: FormlyFieldConfig[] = [];

    const checkFieldDependencies = (fields: FormlyFieldConfig[]) => {
      for (const field of fields) {
        if (field?.templateOptions?.query?.rules) {
          const dependsOnChangedField = this.fieldDependsOnChanges(field, changedFields);
          if (dependsOnChangedField) {
            affectedFields.push(field);
          }
        }

        if (field.fieldGroup) {
          checkFieldDependencies(field.fieldGroup);
        }
        if (field.fieldArray?.fieldGroup) {
          checkFieldDependencies(field.fieldArray.fieldGroup);
        }
      }
    };

    checkFieldDependencies(this.fields);
    return affectedFields;
  }

  private fieldDependsOnChanges(field: FormlyFieldConfig, changedFields: Set<string>): boolean {
    const rules = field.templateOptions?.query?.rules;
    if (!rules) return false;

    for (const rule of rules) {
      if ('field' in rule) {
        const fieldPath = this.buildFieldPath(rule.field, rule.parents);
        if (changedFields.has(fieldPath)) {
          return true;
        }
      }
    }

    return false;
  }

  private buildFieldPath(field: string, parents?: string[]): string {
    if (!parents || parents.length === 0) return field;
    return [...parents, field].join('.');
  }

  private updateConditionalFields(fields: FormlyFieldConfig[]): Observable<void> {
    return new Observable(observer => {
      // Process fields in batches to avoid blocking the UI
      const batchSize = 10;
      let processed = 0;

      const processBatch = () => {
        const batch = fields.slice(processed, processed + batchSize);

        batch.forEach(field => {
          this.updateFieldVisibility(field);
        });

        processed += batchSize;

        if (processed < fields.length) {
          setTimeout(processBatch, 10); // Small delay to prevent UI blocking
        } else {
          this.cdr.markForCheck();
          observer.next();
          observer.complete();
        }
      };

      processBatch();
    });
  }

  private updateFieldVisibility(field: FormlyFieldConfig): void {
    if (!field?.templateOptions?.query) return;

    const query = field.templateOptions.query;
    const sectionIndex = this.getSectionIndexOfField(field.id, this.fields) ?? 0;
    const isCustomerForm = this.isIntakeForm;

    const hidden = this.evaluateQueryOptimized(query, isCustomerForm, query.showWhenTrue, sectionIndex);

    if (field.templateOptions.hidden !== hidden) {
      field.templateOptions.hidden = hidden;
    }
  }

  private evaluateQueryOptimized(query: Query, isCustomerForm: boolean, showWhenTrue = false, currentSectionIndex: number): boolean {
    if (!query.rules || query.rules.length === 0) {
      return false;
    }
    const fieldValueMap = new Map<string, any[]>();
    const rulesWithFields = query.rules.filter((r): r is QueryRule => 'field' in r);

    const cacheKey = this.createEfficientCacheKey(query, isCustomerForm, currentSectionIndex, fieldValueMap);

    const cached = this.queryResultCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.result;
    }

    rulesWithFields.forEach(rule => {
      const valueCacheKey = `${rule.field}-${rule.parents?.join('.')}-${currentSectionIndex}-${isCustomerForm}`;

      let values = this.fieldValueCache.get(valueCacheKey);
      if (!values || Date.now() - values.timestamp > this.CACHE_TTL) {
        values = {
          values: this.getValuesOptimized(rule.field, rule.parents, isCustomerForm, currentSectionIndex),
          timestamp: Date.now(),
          ttl: this.CACHE_TTL
        };
        this.fieldValueCache.set(valueCacheKey, values);
      }

      fieldValueMap.set(`${rule.field}-${rule.parents?.join('.')}-${currentSectionIndex}`, values.values);
    });

    const result = this.evaluateQueryWithEarlyTermination(query, fieldValueMap, isCustomerForm, currentSectionIndex);
    const finalResult = query.showWhenTrue ? !result : result;
    this.queryResultCache.set(cacheKey, {
      result: finalResult,
      timestamp: Date.now(),
      ttl: this.CACHE_TTL
    });

    this.evictOldCacheEntries();

    return finalResult;
  }

  private getValuesOptimized(key: string, parents: string[], isCustomerForm: boolean, currentSectionIndex: number, repeatIndex = 0): any[] {
    if (!this.model || !key) return [];

    const cacheKey = `${key}-${parents?.join('.')}-${currentSectionIndex}-${repeatIndex}-${isCustomerForm}`;

    const cached = this.fieldValueCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.values;
    }

    // Optimized value extraction
    const result = this.extractFieldValue(key, parents, isCustomerForm, currentSectionIndex, repeatIndex);

    this.fieldValueCache.set(cacheKey, {
      values: result,
      timestamp: Date.now(),
      ttl: this.CACHE_TTL
    });

    return result;
  }

  private extractFieldValue(key: string, parents: string[], isCustomerForm: boolean, currentSectionIndex: number, repeatIndex = 0): any[] {
    try {
      let valueObj = this.model;

      // Navigate through parent objects
      for (const parent of parents || []) {
        if (isCustomerForm && Array.isArray(valueObj)) {
          valueObj = valueObj[0]?.[parent];
        } else {
          valueObj = valueObj?.[parent];
        }

        if (valueObj === undefined || valueObj === null) {
          return [];
        }
      }

      // Extract final value
      if (isCustomerForm && Array.isArray(valueObj)) {
        return [valueObj[currentSectionIndex]?.[key]].filter(v => v !== undefined);
      } else {
        return [valueObj?.[key]].filter(v => v !== undefined);
      }
    } catch {
      return [];
    }
  }

  private evaluateQueryWithEarlyTermination(query: Query, fieldValueMap: Map<string, any[]>, isCustomerForm: boolean, currentSectionIndex: number): boolean {
    let queryMatched = query.condition === 'and';

    for (const ruleOrQuery of query.rules) {
      let result = false;

      if ('rules' in ruleOrQuery) {
        result = this.evaluateQueryOptimized(ruleOrQuery as Query, isCustomerForm, false, currentSectionIndex);
      } else {
        const rule = ruleOrQuery as QueryRule;
        const cacheKey = `${rule.field}-${rule.parents?.join('.')}-${currentSectionIndex}`;
        const values = fieldValueMap.get(cacheKey) ?? [];

        // Early termination for OR conditions
        if (query.condition === 'or') {
          for (const value of values) {
            if (this.evaluateRule(rule, value)) {
              result = true;
              break;
            }
          }
          if (result) break; // Early termination for OR
        } else {
          // For AND conditions, check all values
          for (const value of values) {
            if (this.evaluateRule(rule, value)) {
              result = true;
              break;
            }
          }
          if (!result) break; // Early termination for AND
        }
      }

      if (query.condition === 'and' && !result) {
        queryMatched = false;
        break;
      } else if (query.condition === 'or' && result) {
        queryMatched = true;
        break;
      }
    }

    return queryMatched;
  }

  private evictOldCacheEntries(): void {
    // Evict old entries when cache gets too large
    if (this.queryResultCache.size > this.MAX_CACHE_SIZE) {
      const entries = Array.from(this.queryResultCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

      const toRemove = entries.slice(0, Math.floor(this.MAX_CACHE_SIZE * 0.2));
      toRemove.forEach(([key]) => this.queryResultCache.delete(key));
    }

    if (this.fieldValueCache.size > this.MAX_CACHE_SIZE) {
      const entries = Array.from(this.fieldValueCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

      const toRemove = entries.slice(0, Math.floor(this.MAX_CACHE_SIZE * 0.2));
      toRemove.forEach(([key]) => this.fieldValueCache.delete(key));
    }
  }

  private setupFormStateTracking(): void {
    // Only track form state for intake forms
    if (!this.fields?.[0] || this.fields[0].type !== 'intake-form') {
      return;
    }

    this.formStateChange$.pipe(takeUntil(this.destroyed$)).subscribe(({ form, valid }) => {
      this.formViewService.changeFormState({ form, valid });
    });

    this.form?.valueChanges.pipe(debounceTime(100), takeUntil(this.destroyed$)).subscribe(() => {
      this.checkFormStateChange();
    });

    this.form?.statusChanges.pipe(debounceTime(100), takeUntil(this.destroyed$)).subscribe(() => {
      this.checkFormStateChange();
    });
  }

  private checkFormStateChange(): void {
    if (!this.form || !this.fields?.[0]) {
      return;
    }

    const currentState = { form: this.form, valid: this.form.valid };

    if (!this.lastFormState || this.lastFormState.valid !== currentState.valid || !this.isValueEqual(this.lastFormState.form.value, currentState.form.value) || this.fields[0].type === 'intake-form') {
      this.lastFormState = currentState;
      this.formStateChange$.next(currentState);
    }
  }

  private isValueEqual(prev: any, curr: any): boolean {
    if (prev === curr) return true;
    if (typeof prev !== typeof curr) return false;
    if (Array.isArray(prev) !== Array.isArray(curr)) return false;

    if (typeof prev === 'object' && prev !== null) {
      const prevKeys = Object.keys(prev);
      const currKeys = Object.keys(curr);
      if (prevKeys.length !== currKeys.length) return false;
      return prevKeys.every(key => this.isValueEqual(prev[key], curr[key]));
    }

    return prev === curr;
  }

  private createEfficientCacheKey(query: Query, isCustomerForm: boolean, currentSectionIndex: number, fieldValueMap): string {
    const valuesUsed = Array.from(fieldValueMap.values());
    const ruleKeys = query.rules
      .map(rule => {
        if ('field' in rule) {
          return `${rule.field}-${rule.operator}-${rule.value}-${rule.parents?.join('.')}`;
        } else {
          return `subquery-${rule.rules?.length}`;
        }
      })
      .join('|');

    return `${ruleKeys}-${valuesUsed.join('-')}-${query.condition}-${query.showWhenTrue}-${isCustomerForm}-${currentSectionIndex}`;
  }
}
